import { useEffect, useRef } from 'react';

import { Map as MapLibreMap } from 'maplibre-gl';

import { Aggregated, PostcodeMarker } from '../CountryView/types';
import { flyToCountryBounds, flyToFeatureBounds } from '../CountryView/utils/flyToCountryBounds';
import { computeBounds } from '../MapSharedData';

interface UseUnifiedNavigationOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  activeCountry: string | null;
  activeRegion: string | null;
  focusedPostcode: string | null;
  geojson: GeoJSON.FeatureCollection | null;
  regionNameProperty: string;
  postcodeProperty: string;
  /** Polygons loaded for the active region (null = dots-only). */
  activePolygons: GeoJSON.FeatureCollection | null;
  allMarkers: PostcodeMarker[];
  aggregated: Aggregated;
  /** True if a polygon URL is resolvable for the active region. Lets the
   *  region-open camera move wait for polygons to load instead of firing
   *  immediately and snapping again once polygons arrive. */
  hasPolygonsForActive: boolean;
}

/**
 * In-country zoom navigation for REFACTOR_PLAN.md step 5. The drill-in /
 * drill-out hooks own the country-level transitions; this one handles the
 * four moves the user can make while inside a country:
 *
 *   1. Postcode focus opened  → fit polygon (maxZoom 15) or fly to marker
 *   2. Postcode closed        → zoom OUT to the active region
 *   3. Region opened          → zoom IN to the region (maxZoom 10)
 *   4. Region closed          → zoom OUT to the active-incident bounds of
 *                                the country (NOT the whole country — same
 *                                rule as the initial drill-in framing)
 *
 * Refs guard against firing on the initial mount, when prev=current=null.
 * The hook deliberately ignores activeCountry transitions (drill-in/out
 * sequence those camera moves); it only fires when activeRegion or
 * focusedPostcode change while a country is open.
 */
export function useUnifiedNavigation({
  mapRef,
  ready,
  activeCountry,
  activeRegion,
  focusedPostcode,
  geojson,
  regionNameProperty,
  postcodeProperty,
  activePolygons,
  allMarkers,
  aggregated,
  hasPolygonsForActive,
}: UseUnifiedNavigationOptions) {
  const prevActiveRegionRef = useRef<string | null>(null);
  const prevFocusedPostcodeRef = useRef<string | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !activeCountry || !geojson) {
      prevActiveRegionRef.current = activeRegion;
      prevFocusedPostcodeRef.current = focusedPostcode;
      return;
    }

    const prevRegion = prevActiveRegionRef.current;
    const prevPostcode = prevFocusedPostcodeRef.current;

    const regionChanged = prevRegion !== activeRegion;
    const postcodeChanged = prevPostcode !== focusedPostcode;
    if (!regionChanged && !postcodeChanged) return;

    // Refs are only advanced when a branch actually acts. If a branch
    // defers (case 3 waiting on polygons), refs stay unchanged and the
    // effect will re-fire as a fresh region change once polygons arrive.
    const commit = () => {
      prevActiveRegionRef.current = activeRegion;
      prevFocusedPostcodeRef.current = focusedPostcode;
    };

    // 1. Postcode focus opened — zoom to polygon if available, else fly to
    //    the marker coordinates with a fixed zoom.
    if (focusedPostcode && (postcodeChanged || regionChanged)) {
      if (activePolygons) {
        const norm = (s: string) => s.trim().toUpperCase().replace(/[\s-]/g, '');
        const target = norm(focusedPostcode);
        const f = activePolygons.features.find((feat) => {
          const polyCode = norm(String(feat.properties?.[postcodeProperty] ?? ''));
          return polyCode === target || target.startsWith(polyCode);
        });
        if (f) {
          const bounds = computeBounds({ type: 'FeatureCollection', features: [f] });
          if (bounds) {
            map.fitBounds(bounds, { padding: 80, duration: 700, maxZoom: 15 });
            commit();
            return;
          }
        }
      }
      const marker = allMarkers.find((m) => m.postcode === focusedPostcode);
      if (marker && Number.isFinite(marker.lng) && Number.isFinite(marker.lat)) {
        map.flyTo({ center: [marker.lng, marker.lat], zoom: 13, duration: 700, essential: true });
      }
      commit();
      return;
    }

    // 2. Postcode closed but region still active — zoom OUT to the region.
    if (postcodeChanged && !focusedPostcode && activeRegion) {
      const feature = geojson.features.find(
        (f) => f.properties?.[regionNameProperty] === activeRegion,
      );
      if (feature) flyToFeatureBounds({ map, feature, duration: 700 });
      commit();
      return;
    }

    // 3. Region opened — zoom IN to the region.
    if (regionChanged && activeRegion) {
      // If postcode polygons are expected for this region but haven't
      // landed yet, defer the camera move so it lands at the same moment
      // the polygons appear — otherwise the camera snaps in while the
      // polygons arrive a tick later and the transition looks jerky.
      //
      // We deliberately DON'T check polygonsLoading here: in the same
      // render cycle as the click, loadForRegion's setLoading(true) is
      // still queued — polygonsLoading reads false. The synchronous
      // signal is `hasPolygonsForActive && !activePolygons` — URL is
      // resolvable and we don't have polygons yet, no matter who's
      // about to kick off the fetch.
      //
      // Refs intentionally NOT committed: the effect will re-fire when
      // activePolygons becomes non-null, treating this as the same
      // region-change event and firing the fly at that point.
      if (hasPolygonsForActive && !activePolygons) {
        return;
      }
      const feature = geojson.features.find(
        (f) => f.properties?.[regionNameProperty] === activeRegion,
      );
      if (feature) flyToFeatureBounds({ map, feature, duration: 800 });
      commit();
      return;
    }

    // 4. Region closed — frame the active-incident regions of the country,
    //    not the whole country. On big countries (Russia, Canada) the full
    //    extent would fly the camera off into empty space.
    if (regionChanged && !activeRegion) {
      const activeFeatures = geojson.features.filter((f) => {
        const name = f.properties?.[regionNameProperty];
        return name && name !== 'Unassigned' && (aggregated[name]?.total ?? 0) > 0;
      });
      const target =
        activeFeatures.length > 0
          ? computeBounds({ type: 'FeatureCollection', features: activeFeatures })
          : computeBounds(geojson);
      if (target) {
        flyToCountryBounds({ map, bounds: target, countryCode: activeCountry, duration: 800 });
      }
      commit();
    }
  }, [
    mapRef,
    ready,
    activeCountry,
    activeRegion,
    focusedPostcode,
    geojson,
    regionNameProperty,
    postcodeProperty,
    activePolygons,
    allMarkers,
    aggregated,
    hasPolygonsForActive,
  ]);
}
